import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Users, FileText, TrendingUp, FolderKanban, DollarSign, Target,
  Plus, ArrowRight, AlertTriangle, Clock, CheckCircle, Bell, ListTodo,
  Trash2, Waves, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium' });
  const [announcements, setAnnouncements] = useState([]);
  const [annDialog, setAnnDialog] = useState(false);
  const [annForm, setAnnForm] = useState({ title: '', content: '', priority: 'normal' });
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/tasks').then(r => setTasks(r.data)).catch(() => {});
    api.get('/company/announcements').then(r => setAnnouncements(r.data)).catch(() => {});
  }, []);

  // Refresh dashboard stats after any task mutation
  const refreshDashboard = () => {
    api.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/tasks').then(r => setTasks(r.data)).catch(() => {});
  };

  const handleAddTask = async () => {
    await api.post('/tasks', newTask);
    toast.success('Task created');
    setAddTaskOpen(false); setNewTask({ title: '', description: '', priority: 'medium' });
    refreshDashboard();
  };
  const handleToggleTask = async (task) => {
    if (task.auto) return;
    await api.put(`/tasks/${task.id}`, { status: task.status === 'done' ? 'pending' : 'done' });
    refreshDashboard();
  };
  const handleDeleteTask = async (id) => {
    await api.delete(`/tasks/${id}`);
    refreshDashboard();
  };

  if (!stats) return (
    <div className="p-6 lg:p-8"><div className="h-36 bg-white rounded-xl animate-pulse mb-4" /><div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)}</div></div>
  );

  const pendingTasks = tasks.filter(t => t.status !== 'done');
  const highPriority = pendingTasks.filter(t => t.priority === 'high');

  const kpis = [
    { label: 'Leads', value: stats.total_leads, icon: Users, color: 'text-blue-600 bg-blue-50', sub: `${stats.leads_by_stage?.new || 0} new` },
    { label: 'Bids', value: stats.total_bids, icon: FileText, color: 'text-amber-600 bg-amber-50', sub: `${stats.bids_sent} sent` },
    { label: 'Win Rate', value: `${stats.win_rate}%`, icon: Target, color: 'text-orange-600 bg-orange-50', sub: `${stats.bids_won} won` },
    { label: 'Avg Margin', value: `${stats.avg_margin}%`, icon: TrendingUp, color: 'text-violet-600 bg-violet-50', sub: 'gross' },
    { label: 'Projects', value: stats.active_projects, icon: FolderKanban, color: 'text-cyan-600 bg-cyan-50', sub: 'active' },
    { label: 'Revenue', value: `$${(stats.total_revenue / 1000).toFixed(0)}k`, icon: DollarSign, color: 'text-emerald-600 bg-emerald-50', sub: 'total' },
  ];

  const priorityBorder = { high: 'border-l-red-400', medium: 'border-l-amber-300', low: 'border-l-gray-200' };
  const priorityBadge = { high: 'bg-red-50 text-red-600 border-red-100', medium: 'bg-amber-50 text-amber-600 border-amber-100', low: 'bg-gray-50 text-gray-500 border-gray-100' };
  const typeIcon = { approval: <AlertTriangle className="w-4 h-4 text-red-400" />, overdue: <Clock className="w-4 h-4 text-red-400" />, upcoming: <Clock className="w-4 h-4 text-amber-400" />, info: <Bell className="w-4 h-4 text-blue-400" /> };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1360px] mx-auto" data-testid="dashboard-page">

      {/* Greeting */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl text-[#0F1D32]">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 text-sm mt-1 font-medium" style={{fontFamily:'Manrope',fontStyle:'normal'}}>Here's what's happening with your projects</p>
        </div>
        <div className="flex gap-2">
          {stats.pending_approvals > 0 && user?.role === 'admin' && (
            <Button variant="outline" className="border-red-200 text-red-600 bg-red-50 hover:bg-red-100 text-sm rounded-sm" onClick={() => navigate('/bids')} data-testid="pending-approvals-btn">
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />{stats.pending_approvals} Approval{stats.pending_approvals > 1 ? 's' : ''}
            </Button>
          )}
          <Button onClick={() => navigate('/bids/new')} className="bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wider text-xs rounded-sm shadow-sm" data-testid="dashboard-new-bid-btn">
            <Plus className="w-4 h-4 mr-1" /> New Bid
          </Button>
        </div>
      </div>

      {/* KPIs — Dark Navy Panels */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {kpis.map((kpi, i) => (
          <Card key={kpi.label} className={`animate-fade-in stagger-${i+1} bg-[#0F1D32] border-white/10 rounded-sm hover:border-amber-500/30 transition-all duration-300`}>
            <CardContent className="p-4">
              <div className="w-8 h-8 rounded-sm bg-white/10 flex items-center justify-center mb-3">
                <kpi.icon className="w-4 h-4 text-amber-400" />
              </div>
              <p className="text-2xl font-bold text-white font-mono tracking-tight leading-none">{kpi.value}</p>
              <p className="text-[11px] text-white/40 mt-1 font-medium" style={{fontFamily:'Manrope',fontStyle:'normal'}}>{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Announcements */}
      {announcements.length > 0 && (
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center"><Bell className="w-3.5 h-3.5 text-amber-500" /></div>
                <p className="text-sm font-bold text-[#1e3a50]">Announcements</p>
              </div>
              {user?.role === 'admin' && (
                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-[#4a9ac5] font-bold" onClick={() => setAnnDialog(true)}>
                  <Plus className="w-3 h-3 mr-0.5" /> Post
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {announcements.slice(0, 3).map(ann => (
                <div key={ann.id} className={`p-3 rounded-lg border-l-[3px] ${ann.priority === 'urgent' ? 'border-l-red-400 bg-red-50/50' : 'border-l-[#4a9ac5] bg-[#f5f9fb]'}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-[#1e3a50]">{ann.title}</p>
                    <span className="text-[9px] font-medium text-[#6b8a9e]">{ann.author_name} | {new Date(ann.created_at).toLocaleDateString()}</span>
                  </div>
                  {ann.content && <p className="text-[11px] font-medium text-[#4a6a7e] mt-1">{ann.content}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Announcement Dialog */}
      <Dialog open={annDialog} onOpenChange={setAnnDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Post Announcement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Title *</Label><Input value={annForm.title} onChange={e => setAnnForm(p => ({...p, title: e.target.value}))} placeholder="Schedule change, safety alert..." /></div>
            <div><Label className="text-xs font-bold">Message</Label><Textarea value={annForm.content} onChange={e => setAnnForm(p => ({...p, content: e.target.value}))} rows={3} /></div>
            <div><Label className="text-xs font-bold">Priority</Label>
              <Select value={annForm.priority} onValueChange={v => setAnnForm(p => ({...p, priority: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnDialog(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={async () => {
              await api.post('/company/announcements', annForm); toast.success('Posted');
              setAnnDialog(false); setAnnForm({ title: '', content: '', priority: 'normal' });
              api.get('/company/announcements').then(r => setAnnouncements(r.data));
            }} disabled={!annForm.title.trim()}>Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — Tasks */}
        <div className="lg:col-span-2 space-y-6">
          <Card className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${highPriority.length > 0 ? 'ring-1 ring-red-100' : ''}`} data-testid="tasks-section">
            <CardHeader className="pb-3 px-5 pt-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${highPriority.length > 0 ? 'bg-red-50' : 'bg-blue-50'}`}>
                    <ListTodo className={`w-4 h-4 ${highPriority.length > 0 ? 'text-red-500' : 'text-blue-500'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-sm font-bold text-gray-900">Action Items</CardTitle>
                    <p className="text-[10px] text-gray-400">{pendingTasks.length} pending</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-8 text-xs border-gray-200" onClick={() => setAddTaskOpen(true)} data-testid="add-task-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Task
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              {pendingTasks.length === 0 ? (
                <div className="text-center py-10"><CheckCircle className="w-10 h-10 text-emerald-200 mx-auto mb-2" /><p className="text-sm text-gray-400">All caught up!</p></div>
              ) : (
                <div className="space-y-1">
                  {pendingTasks.slice(0, 7).map(task => (
                    <div key={task.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border-l-[3px] ${priorityBorder[task.priority]} hover:bg-gray-50 transition-colors group`} data-testid={`task-${task.id}`}>
                      {!task.auto ? <Checkbox checked={task.status === 'done'} onCheckedChange={() => handleToggleTask(task)} /> : <div className="flex-shrink-0">{typeIcon[task.type] || <Bell className="w-4 h-4 text-gray-300" />}</div>}
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => task.link && navigate(task.link)}>
                        <p className="text-[13px] font-medium text-gray-700 truncate">{task.title}</p>
                        {task.description && <p className="text-[10px] text-gray-400 truncate">{task.description}</p>}
                      </div>
                      <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${priorityBadge[task.priority]}`}>{task.priority}</Badge>
                      {!task.auto && <button className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleDeleteTask(task.id)}><Trash2 className="w-3.5 h-3.5 text-gray-300 hover:text-red-400" /></button>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Bids */}
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-3 px-5 pt-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><FileText className="w-4 h-4 text-amber-500" /></div>
                  <CardTitle className="text-sm font-bold text-gray-900">Recent Bids</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/bids')} className="text-[#4a9ac5] text-xs h-7">View All <ArrowRight className="w-3 h-3 ml-1" /></Button>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="space-y-1.5">
                {(stats.recent_bids || []).map(bid => (
                  <div key={bid.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group" onClick={() => navigate(`/bids/${bid.id}`)} data-testid={`recent-bid-${bid.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-gray-800 truncate">{bid.inputs?.customer_name || 'Untitled'}</p>
                      <p className="text-[10px] text-gray-400">{bid.inputs?.city}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold font-mono text-gray-800">${(bid.totals?.total_price || 0).toLocaleString()}</span>
                      <Badge variant="outline" className={`text-[10px] ${bid.status === 'won' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : bid.status === 'sent' ? 'bg-blue-50 text-blue-600 border-blue-100' : bid.status === 'lost' ? 'bg-red-50 text-red-500 border-red-100' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>{bid.status}</Badge>
                      <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-[#0c8ce9] transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right — Pipeline + Quick Actions */}
        <div className="space-y-6">
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-3 px-5 pt-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center"><Users className="w-4 h-4 text-violet-500" /></div>
                  <CardTitle className="text-sm font-bold text-gray-900">Pipeline</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} className="text-[#4a9ac5] text-xs h-7"><ArrowRight className="w-3 h-3" /></Button>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-5 space-y-2.5">
              {Object.entries(stats.leads_by_stage || {}).filter(([_, v]) => v > 0).map(([stage, count]) => {
                const total = stats.total_leads || 1;
                const pct = Math.round((count / total) * 100);
                const colors = { new:'bg-gray-400', contacted:'bg-blue-400', site_visit:'bg-violet-400', design:'bg-amber-400', bid_sent:'bg-orange-400', negotiation:'bg-pink-400', won:'bg-emerald-500', lost:'bg-red-400' };
                return (
                  <div key={stage}>
                    <div className="flex items-center justify-between mb-1"><span className="text-xs font-medium text-gray-500 capitalize">{stage.replace('_', ' ')}</span><span className="text-xs font-bold font-mono text-gray-700">{count}</span></div>
                    <div className="h-1 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${colors[stage] || 'bg-gray-300'}`} style={{ width: `${Math.max(pct, 8)}%` }} /></div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-2 px-5 pt-5"><CardTitle className="text-sm font-bold text-gray-900">Quick Actions</CardTitle></CardHeader>
            <CardContent className="px-5 pb-5 grid grid-cols-2 gap-2">
              {[
                { label: 'New Lead', icon: Users, to: '/leads', color: 'bg-blue-50 text-blue-600 hover:bg-blue-100' },
                { label: 'New Bid', icon: FileText, to: '/bids/new', color: 'bg-amber-50 text-amber-600 hover:bg-amber-100' },
                { label: 'Jobs Board', icon: FolderKanban, to: '/jobs', color: 'bg-violet-50 text-violet-600 hover:bg-violet-100' },
                { label: 'Finance', icon: DollarSign, to: '/finance', color: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100' },
              ].map(a => (
                <button key={a.label} onClick={() => navigate(a.to)} className={`flex items-center gap-2 p-3 rounded-lg text-xs font-semibold transition-all ${a.color}`}>
                  <a.icon className="w-4 h-4" /> {a.label}
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Task Dialog */}
      <Dialog open={addTaskOpen} onOpenChange={setAddTaskOpen}>
        <DialogContent className="max-w-sm" data-testid="add-task-dialog">
          <DialogHeader><DialogTitle>Add Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Task *</Label><Input value={newTask.title} onChange={e => setNewTask(p => ({...p, title: e.target.value}))} placeholder="What needs to be done?" data-testid="task-title-input" /></div>
            <div><Label>Details</Label><Input value={newTask.description} onChange={e => setNewTask(p => ({...p, description: e.target.value}))} placeholder="Optional notes..." /></div>
            <div><Label>Priority</Label>
              <Select value={newTask.priority} onValueChange={v => setNewTask(p => ({...p, priority: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTaskOpen(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={handleAddTask} disabled={!newTask.title.trim()} data-testid="task-save-btn">Add Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
